import {Button, Col, Container, FloatingLabel, Form, Modal, Row, Tab, Tabs} from "react-bootstrap";
import React, {useContext, useEffect, useRef, useState} from "react";
import {UsacmContext} from "../App";
import {deleteCourse, upsertCourse} from "../api/CourseApi";
import {hasPermission} from "../api/UserApi";
import {CourseDocuments} from "./CourseDocuments";
import {CourseInstructors} from "./CourseInstructors";
import {CoursePages} from "./CoursePages";
import {CoursePricing} from "./CoursePricing";
import {CourseStudents} from "./CourseStudents";
import {CourseValidate} from "./CourseValidate";
import {CourseVideos} from "./CourseVideos";
import {HtmlEditor} from "../shared/HtmlEditor";
import {fieldHasErrors, getErrorMessageForField} from "../utils/formUtils";
import {getConfLabel, makePlural, showSuccessToast, titleCase} from "../utils/usacmUtils";
import {
  CONF_LABEL_KEY_COURSE,
  CONF_LABEL_KEY_COURSE_PAGE,
  COURSE_STATUS_DRAFT,
  COURSE_STATUS_NAMES,
  COURSE_STATUSES,
  PERMISSION_CONF_ADMIN,
  PERMISSION_COURSE_ADMIN,
  PERMISSION_STAFF
} from "../constants";

export function CourseEdit({origCourse, onClose, onSaved}) {
  const context = useContext(UsacmContext);
  const [conf,] = context.conference;
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [courseStatus, setCourseStatus] = useState(COURSE_STATUS_DRAFT);
  const [imageUrl, setImageUrl] = useState('');
  const [courseOrder, setCourseOrder] = useState('');
  const [errors, setErrors] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // The other tabs all hang off a saved course, so they stay off until the course has been created
  const isNew = !origCourse?.id;
  const editorRef = useRef(null);
  // Only a course admin says who the instructors are, sets the prices (and creates/deletes courses)
  const isCourseAdmin = hasPermission(PERMISSION_STAFF) || hasPermission(PERMISSION_CONF_ADMIN) ||
    hasPermission(PERMISSION_COURSE_ADMIN);
  // Course admins can edit any course, an instructor only the ones they are assigned to (the server works
  // this out and sends it back as can_edit).  A course being created is always editable.
  const canEdit = origCourse?.id ? !!origCourse?.can_edit : isCourseAdmin;
  // A course which has been bought or granted cannot be deleted, it has to be archived instead
  const canDelete = isCourseAdmin && !isNew && !origCourse?.access_count && !origCourse?.order_count;
  const courseLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE));
  const pageLabel = titleCase(getConfLabel(conf, CONF_LABEL_KEY_COURSE_PAGE));

  useEffect(() => {
    if (origCourse) {
      setTitle(origCourse.title || '');
      setSubtitle(origCourse.subtitle || '');
      setCourseStatus(origCourse.course_status || COURSE_STATUS_DRAFT);
      setImageUrl(origCourse.image_url || '');
      setCourseOrder(origCourse.course_order === null ? '' : origCourse.course_order);
      if (editorRef.current) {
        editorRef.current.setContent(origCourse.description || '');
      }
    }
  }, [origCourse]);

  function callUpsertCourse() {
    const description = editorRef.current ? editorRef.current.getContent() : '';
    upsertCourse(origCourse?.id, title, subtitle, description, courseStatus, imageUrl, courseOrder, (code, data, newErrors) => {
        if (code === 200) {
          showSuccessToast(`${origCourse?.id ? 'Updated' : 'Created'} ${courseLabel.toLowerCase()}.`);
          // Stay in the editor on the saved course
          setErrors([]);
          onSaved(data);
          if (!origCourse?.id) {
            // A new course is empty - go to its pages so they can be added next
            setActiveTab('pages');
          }
        } else {
          setErrors(newErrors);
        }
      });
  }

  function callDeleteCourse() {
    deleteCourse(origCourse?.id, (code, data, newErrors) => {
      setShowDeleteConfirm(false);
      if (code === 200) {
        showSuccessToast(`Deleted ${courseLabel.toLowerCase()}.`);
        setErrors([]);
        onClose();
      } else {
        setErrors(newErrors);
      }
    });
  }

  return (
    <Container fluid className="usacm-container-wide pt-2">

      <Row className="mb-2">
        <Col className="col-12 text-center fw-bold">
          {origCourse?.id ? `Editing ${origCourse.title}` : `Add ${courseLabel}`}
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onSelect={key => setActiveTab(key)}
        id="course-edit-tabs"
        className="mb-3"
        mountOnEnter={true}
      >
        <Tab eventKey="details" title="Details">
          <Container fluid className="usacm-container-narrow">

            <Row className="mb-3">
              <Col>
                <Form.Group controlId="title">
                  <FloatingLabel controlId="title" label="Title">
                    <Form.Control type="text"
                                  placeholder="Title"
                                  name="title"
                                  value={title}
                                  disabled={!canEdit}
                                  onChange={e => setTitle(e.target.value)}
                                  isInvalid={fieldHasErrors(errors, 'title')}/>
                    <Form.Control.Feedback type="invalid">
                      {getErrorMessageForField(errors, 'title')}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Group controlId="subtitle">
                  <FloatingLabel controlId="subtitle" label="Subtitle">
                    <Form.Control type="text"
                                  placeholder="Subtitle"
                                  name="subtitle"
                                  value={subtitle}
                                  disabled={!canEdit}
                                  onChange={e => setSubtitle(e.target.value)}
                                  isInvalid={fieldHasErrors(errors, 'subtitle')}/>
                    <Form.Control.Feedback type="invalid">
                      {getErrorMessageForField(errors, 'subtitle')}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Group controlId="description">
                  <div>Description (shown in the catalog)</div>
                  <div style={fieldHasErrors(errors, 'description') ? {border: '1px solid red'} : {}}>
                    <HtmlEditor
                      initialValue={origCourse?.description || ''}
                      onInit={(evt, editor) => editorRef.current = editor}
                      disabled={!canEdit}
                    />
                  </div>
                  {fieldHasErrors(errors, 'description') &&
                    <div className="text-center mb-3 usacm-error-message">
                      {getErrorMessageForField(errors, 'description')}
                    </div>
                  }
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <Form.Group controlId="image_url">
                  <FloatingLabel controlId="image_url" label="Image URL">
                    <Form.Control type="text"
                                  placeholder="Image URL"
                                  name="image_url"
                                  value={imageUrl}
                                  disabled={!canEdit}
                                  onChange={e => setImageUrl(e.target.value)}
                                  isInvalid={fieldHasErrors(errors, 'image_url')}/>
                    <Form.Control.Feedback type="invalid">
                      {getErrorMessageForField(errors, 'image_url')}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col className="col-6">
                <Form.Group controlId="course_status">
                  <FloatingLabel controlId="course_status" label="Status">
                    <Form.Control
                      className="form-select"
                      as="select"
                      value={courseStatus}
                      disabled={!canEdit}
                      onChange={e => setCourseStatus(e.target.value)}
                      isInvalid={fieldHasErrors(errors, 'course_status')}
                    >
                      {COURSE_STATUSES.map(status =>
                        <option value={status} key={status}>{COURSE_STATUS_NAMES[status]}</option>)}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {getErrorMessageForField(errors, 'course_status')}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col className="col-6">
                <Form.Group controlId="course_order">
                  <FloatingLabel controlId="course_order" label="Order in Catalog">
                    <Form.Control type="number"
                                  step="1"
                                  placeholder="Order in Catalog"
                                  name="course_order"
                                  value={courseOrder}
                                  disabled={!canEdit}
                                  onChange={e => setCourseOrder(e.target.value)}
                                  isInvalid={fieldHasErrors(errors, 'course_order')}/>
                    <Form.Control.Feedback type="invalid">
                      {getErrorMessageForField(errors, 'course_order')}
                    </Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            {fieldHasErrors(errors, '') &&
              <Row>
                <Col className="text-center mb-3 usacm-error-message">
                  {getErrorMessageForField(errors, '')}
                </Col>
              </Row>
            }

            <Row>
              <Col className="usacm-button-row">
                <Button variant="secondary" onClick={() => onClose()}>
                  {origCourse?.id ? 'Course List' : 'Cancel'}
                </Button>
                {canDelete &&
                  <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                    Delete {courseLabel}
                  </Button>
                }
                {canEdit &&
                  <Button onClick={() => callUpsertCourse()}>
                    {origCourse?.id ? `Update ${courseLabel}` : `Create ${courseLabel}`}
                  </Button>
                }
              </Col>
            </Row>

          </Container>
        </Tab>

        <Tab eventKey="pages" disabled={isNew} title={makePlural(pageLabel)}>
          {/* The content is on the pages, not on the course - a course is the list of pages */}
          <CoursePages course={origCourse} canEdit={canEdit} onClose={onClose}/>
        </Tab>

        <Tab eventKey="docs" disabled={isNew} title="Docs">
          <CourseDocuments course={origCourse} canEdit={canEdit} onClose={onClose}/>
        </Tab>

        <Tab eventKey="videos" disabled={isNew} title="Videos">
          <CourseVideos course={origCourse} canEdit={canEdit} onClose={onClose}/>
        </Tab>

        <Tab eventKey="pricing" disabled={isNew} title="Pricing">
          {/* Instructors see the prices read-only - only a course admin sets them */}
          <CoursePricing course={origCourse} canEdit={isCourseAdmin} onClose={onClose}/>
        </Tab>

        <Tab eventKey="instructors" disabled={isNew} title="Instructors">
          {/* Instructors can edit this course - only a course admin can say who they are */}
          <CourseInstructors course={origCourse} canAssign={isCourseAdmin} onClose={onClose}/>
        </Tab>

        <Tab eventKey="students" disabled={isNew} title="Students">
          <CourseStudents course={origCourse} onClose={onClose}/>
        </Tab>

        <Tab eventKey="validate" disabled={isNew} title="Validate">
          {/* The checks re-run each time this tab is opened, so they see changes made on the other tabs */}
          <CourseValidate course={origCourse} isActive={activeTab === 'validate'}
                          onGoToTab={tab => setActiveTab(tab)} onClose={onClose}/>
        </Tab>
      </Tabs>

      <Modal show={showDeleteConfirm}
             onHide={() => setShowDeleteConfirm(false)}
             size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete "{origCourse?.title}"?
          This will also delete its pages, prices and coupons.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => callDeleteCourse()}>Delete {courseLabel}</Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
